import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, RotateCcw, Save, Tags, Loader2, Megaphone, Swords, Ban } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const uid = () => Math.random().toString(36).slice(2, 10);
const toList = (s) => s.split(",").map((k) => k.trim()).filter(Boolean);

function Section({ icon: Icon, title, hint, action, children }) {
  return (
    <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex gap-3">
          <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-red/10 text-brand-red">
            <Icon className="h-4 w-4" />
          </span>
          <div>
            <h3 className="font-heading text-lg font-bold text-brand-ink">{title}</h3>
            <p className="mt-1 max-w-2xl text-sm text-brand-mute">{hint}</p>
          </div>
        </div>
        {action}
      </div>
      <div className="mt-5 space-y-4">{children}</div>
    </div>
  );
}

function BlockRow({ block, onChange, onRemove, canEdit, testid }) {
  return (
    <div className="rounded-lg border border-border bg-brand-bg/40 p-4" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className="flex-1 space-y-3">
          <Input
            value={block.title}
            disabled={!canEdit}
            onChange={(e) => onChange({ ...block, title: e.target.value })}
            placeholder="Theme name: e.g. Costs families feel"
            className="font-semibold"
            data-testid={`${testid}-title`}
          />
          <Textarea
            value={block.body}
            disabled={!canEdit}
            rows={3}
            onChange={(e) => onChange({ ...block, body: e.target.value })}
            placeholder="How the campaign talks about it. The guidance every draft should follow."
            data-testid={`${testid}-body`}
          />
          <Input
            value={(block.keywords || []).join(", ")}
            disabled={!canEdit}
            onChange={(e) => onChange({ ...block, keywords: toList(e.target.value) })}
            placeholder="Keywords, comma separated"
            className="text-xs"
            data-testid={`${testid}-keywords`}
          />
        </div>
        {canEdit && (
          <Button variant="ghost" size="icon" onClick={onRemove} data-testid={`${testid}-remove`}>
            <Trash2 className="h-4 w-4 text-brand-red" />
          </Button>
        )}
      </div>
    </div>
  );
}

export function ThemesSettings() {
  const { hasRole } = useAuth();
  const canEdit = hasRole("admin", "manager");
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["themes"], queryFn: () => api.get("/themes").then((r) => r.data) });
  const [form, setForm] = useState(null);
  const [retagging, setRetagging] = useState(false);

  useEffect(() => {
    if (data && !form) setForm({ pillars: data.pillars || [], contrast: data.contrast || [], issues: data.issues || [], avoid: data.avoid || [] });
  }, [data, form]);

  const save = useMutation({
    mutationFn: (payload) => api.put("/themes", payload).then((r) => r.data),
    onSuccess: (d) => {
      setForm({ pillars: d.pillars, contrast: d.contrast, issues: d.issues, avoid: d.avoid });
      qc.invalidateQueries({ queryKey: ["themes"] });
      toast.success("Themes saved. New drafts and incoming signals use them immediately");
    },
    onError: (e) => toast.error(apiError(e, "Could not save themes")),
  });

  const restore = async () => {
    if (!window.confirm("Replace every theme with the MI-04 starting set? Your edits will be lost.")) return;
    try {
      const { data: d } = await api.post("/themes/reset");
      setForm({ pillars: d.pillars, contrast: d.contrast, issues: d.issues, avoid: d.avoid });
      qc.invalidateQueries({ queryKey: ["themes"] });
      toast.success("Restored the MI-04 starting set");
    } catch (e) { toast.error(apiError(e)); }
  };

  const retag = async () => {
    setRetagging(true);
    try {
      const { data: d } = await api.post("/themes/retag");
      toast.success(`Re-tagged ${d.retagged} of ${d.scanned} signals against your issue list`);
      qc.invalidateQueries();
    } catch (e) { toast.error(apiError(e)); } finally { setRetagging(false); }
  };

  if (!form) return null;
  const set = (patch) => setForm({ ...form, ...patch });
  const activeIssues = form.issues.filter((i) => i.active).length;

  return (
    <div className="space-y-6" data-testid="themes-settings">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-white px-5 py-4 shadow-sm">
        <p className="text-sm text-brand-mute">
          {data?.updated_by ? <>Last edited by <span className="font-medium text-brand-ink">{data.updated_by}</span></> : "Starting set"}
          {" · "}{form.pillars.length} pillars · {activeIssues} active issues · {form.contrast.length} contrast frames
        </p>
        <div className="flex flex-wrap gap-2">
          {canEdit && (
            <>
              <Button variant="outline" onClick={retag} disabled={retagging} data-testid="retag-signals-btn">
                {retagging ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Tags className="mr-2 h-4 w-4" />} Re-tag signals
              </Button>
              <Button variant="outline" onClick={restore} data-testid="restore-themes-btn">
                <RotateCcw className="mr-2 h-4 w-4" /> Restore MI-04 set
              </Button>
              <Button onClick={() => save.mutate(form)} disabled={save.isPending}
                      className="bg-brand-red text-white hover:bg-brand-redhover" data-testid="save-themes-btn">
                {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save themes
              </Button>
            </>
          )}
        </div>
      </div>

      <Section
        icon={Megaphone}
        title="Message pillars"
        hint="Your core messages, in your words. Every Content Studio draft and rapid response must ladder up to one of these."
        action={canEdit && (
          <Button variant="outline" size="sm" onClick={() => set({ pillars: [...form.pillars, { id: uid(), title: "", body: "", keywords: [] }] })} data-testid="add-pillar-btn">
            <Plus className="mr-1.5 h-4 w-4" /> Add pillar
          </Button>
        )}
      >
        {form.pillars.map((p, i) => (
          <BlockRow
            key={p.id || i}
            block={p}
            canEdit={canEdit}
            testid={`pillar-${i}`}
            onChange={(next) => set({ pillars: form.pillars.map((x, j) => (j === i ? next : x)) })}
            onRemove={() => set({ pillars: form.pillars.filter((_, j) => j !== i) })}
          />
        ))}
      </Section>

      <Section
        icon={Tags}
        title="Issue themes"
        hint="This list IS the tagging taxonomy: incoming news and Bluesky signals are classified against these issues, and the keywords catch anything the AI misses."
        action={canEdit && (
          <Button variant="outline" size="sm" onClick={() => set({ issues: [...form.issues, { id: uid(), label: "", keywords: [], active: true }] })} data-testid="add-issue-btn">
            <Plus className="mr-1.5 h-4 w-4" /> Add issue
          </Button>
        )}
      >
        <div className="space-y-2">
          {form.issues.map((it, i) => (
            <div key={it.id || i} className={cn("flex flex-wrap items-center gap-3 rounded-lg border p-3",
              it.active ? "border-border bg-white" : "border-dashed border-border bg-brand-bg/60")}
              data-testid={`issue-${i}`}>
              <Input
                value={it.label}
                disabled={!canEdit}
                onChange={(e) => set({ issues: form.issues.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })}
                placeholder="Issue label"
                className="w-full max-w-[15rem] font-medium"
                data-testid={`issue-${i}-label`}
              />
              <Input
                value={(it.keywords || []).join(", ")}
                disabled={!canEdit}
                onChange={(e) => set({ issues: form.issues.map((x, j) => (j === i ? { ...x, keywords: toList(e.target.value) } : x)) })}
                placeholder="Keywords that mean this issue, comma separated"
                className="min-w-[12rem] flex-1 text-xs"
                data-testid={`issue-${i}-keywords`}
              />
              <div className="flex items-center gap-2">
                <Switch
                  checked={!!it.active}
                  disabled={!canEdit}
                  onCheckedChange={(v) => set({ issues: form.issues.map((x, j) => (j === i ? { ...x, active: v } : x)) })}
                  data-testid={`issue-${i}-active`}
                />
                <Label className="text-xs text-brand-mute">{it.active ? "Tracked" : "Retired"}</Label>
                {canEdit && (
                  <Button variant="ghost" size="icon" onClick={() => set({ issues: form.issues.filter((_, j) => j !== i) })} data-testid={`issue-${i}-remove`}>
                    <Trash2 className="h-4 w-4 text-brand-red" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section
        icon={Swords}
        title="Contrast themes"
        hint="What we say about the opponent: record and votes only. Drafts are told to drop any claim that cannot be sourced."
        action={canEdit && (
          <Button variant="outline" size="sm" onClick={() => set({ contrast: [...form.contrast, { id: uid(), title: "", body: "", keywords: [] }] })} data-testid="add-contrast-btn">
            <Plus className="mr-1.5 h-4 w-4" /> Add contrast
          </Button>
        )}
      >
        {form.contrast.map((c, i) => (
          <BlockRow
            key={c.id || i}
            block={c}
            canEdit={canEdit}
            testid={`contrast-${i}`}
            onChange={(next) => set({ contrast: form.contrast.map((x, j) => (j === i ? next : x)) })}
            onRemove={() => set({ contrast: form.contrast.filter((_, j) => j !== i) })}
          />
        ))}
      </Section>

      <Section
        icon={Ban}
        title="Do-not-say list"
        hint="Absolute rules handed to the AI on every generation."
        action={canEdit && (
          <Button variant="outline" size="sm" onClick={() => set({ avoid: [...form.avoid, ""] })} data-testid="add-avoid-btn">
            <Plus className="mr-1.5 h-4 w-4" /> Add rule
          </Button>
        )}
      >
        <div className="space-y-2">
          {form.avoid.map((a, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`avoid-${i}`}>
              <Input
                value={a}
                disabled={!canEdit}
                onChange={(e) => set({ avoid: form.avoid.map((x, j) => (j === i ? e.target.value : x)) })}
                placeholder="Something the campaign never says"
                data-testid={`avoid-${i}-input`}
              />
              {canEdit && (
                <Button variant="ghost" size="icon" onClick={() => set({ avoid: form.avoid.filter((_, j) => j !== i) })} data-testid={`avoid-${i}-remove`}>
                  <Trash2 className="h-4 w-4 text-brand-red" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
